import { useState } from 'react'
import {
  CheckSquare, Mail, FileSignature, Phone, Users, MessageSquare,
  ChevronLeft, ChevronRight, Sparkles, Inbox, Timer,
  // legacy exports kept for DashboardDrawer
  CheckCircle2, FileText, Briefcase, Mic, Calendar, Clock,
} from 'lucide-react'
import { useDashboardMyDay } from '../../hooks/useDashboardMyDay'
import EfficiencyChart from './EfficiencyChart'
import { useDashboardEfficiency } from '../../hooks/useDashboardEfficiency'
import { useAuthStore } from '../../stores/authStore'
import { groupDay, BLOCK_SOURCES, BLOCK_META, type DayItemLite } from './todayGrouping'
import type { Interaction, Person, DashCompany, ProjectTrack, Commitment } from './dashboardData'

const SOURCE_ICON: Record<string, any> = { task: CheckSquare, email: Mail, sign: FileSignature, call: Phone, meeting: Users, dm: MessageSquare }

function dayStr(d: Date) { return d.toISOString().slice(0, 10) }
function fmtTime(iso?: string) { if (!iso) return ''; return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
function shiftDay(day: string, delta: number) { const d = new Date(day + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + delta); return dayStr(d) }
function dayLabel(day: string, today: string) {
  if (day === today) return 'Today'
  const d = new Date(day + 'T00:00:00Z')
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function AvatarStack({ people }: { people: { id: string; initials: string; name: string }[] }) {
  if (!people?.length) return null
  return (
    <span className="flex -space-x-1.5">
      {people.slice(0, 3).map((p) => (
        <span key={p.id} title={p.name} className="w-5 h-5 rounded-full bg-primary/80 text-primary-foreground text-[9px] font-bold flex items-center justify-center ring-1 ring-card">{p.initials}</span>
      ))}
    </span>
  )
}

function AgendaRow({ item, last }: { item: any; last: boolean }) {
  const Icon = SOURCE_ICON[item.source] ?? CheckSquare
  const overdue = item.status === 'overdue'
  const time = fmtTime(item.dueAt ?? item.occurredAt)
  const tag = item.source === 'call' ? 'call back' : item.dueAt ? 'due' : null
  return (
    <a href={item.link} className="flex items-center gap-0 px-2 py-2 hover:bg-accent/40 transition rounded-md">
      <span className={`w-12 text-right text-[11px] tabular-nums shrink-0 ${overdue ? 'text-rose-400' : 'text-muted-foreground'}`}>{time || '—'}</span>
      <span className="relative mx-3 shrink-0 self-stretch w-2.5 flex items-center justify-center">
        <span className={`relative z-10 w-2.5 h-2.5 rounded-full ${overdue ? 'bg-rose-400' : item.source === 'meeting' ? 'bg-blue-400' : item.source === 'call' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        {!last && <span className="absolute left-1/2 -translate-x-1/2 top-1/2 w-px h-full bg-border/60" />}
      </span>
      <Icon size={15} className={`mr-2 shrink-0 ${overdue ? 'text-rose-400' : 'text-foreground/70'}`} />
      <span className="flex-1 min-w-0 text-sm truncate text-foreground">{item.title}</span>
      <AvatarStack people={item.participants} />
      {tag && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${overdue ? 'bg-rose-500/15 text-rose-300' : 'bg-amber-500/15 text-amber-300'}`}>{tag}</span>}
    </a>
  )
}

function BlockCard({ source, items }: { source: string; items: any[] }) {
  const Icon = SOURCE_ICON[source] ?? CheckSquare
  return (
    <div className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border/40 flex items-center gap-2 text-xs font-semibold text-foreground">
        <Icon size={15} className="text-foreground/70" />
        {BLOCK_META[source]?.label ?? source}
        <span className="ml-auto text-muted-foreground tabular-nums">{items.length}</span>
      </div>
      {items.map((i) => (
        <a key={i.id} href={i.link} className="flex items-center gap-2 px-4 py-2 border-b border-border/40 last:border-0 hover:bg-accent/40 transition">
          <span className="flex-1 min-w-0">
            <span className={`block text-sm truncate ${i.status === 'overdue' ? 'text-rose-300' : 'text-foreground'}`}>{i.title}</span>
            {i.summary && <span className="block text-[11px] text-muted-foreground truncate">{i.summary}</span>}
          </span>
          <AvatarStack people={i.participants} />
        </a>
      ))}
    </div>
  )
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-xl bg-card ring-1 ring-border/40 px-4 py-3">
      <div className={`text-2xl font-semibold tabular-nums leading-tight ${tone ?? ''}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  )
}

function ConnectPrompt() {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-sm w-full rounded-2xl bg-card ring-1 ring-border/40 px-8 py-10 flex flex-col items-center gap-4 text-center">
        <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center"><Sparkles size={28} className="text-primary" /></div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Connect Google to bring your day to life</h2>
          <p className="mt-2 text-sm text-muted-foreground">Connect your mail and calendar — your tasks, meetings, calls and replies show up here automatically.</p>
        </div>
        <a href="/workspace/mail" className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition">Connect account</a>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { data, loading } = useDashboardMyDay()
  const efficiency = useDashboardEfficiency()
  const user = useAuthStore((s) => s.user)
  const [day, setDay] = useState(() => dayStr(new Date()))

  if (loading) return <div className="flex-1 p-8 space-y-4"><div className="h-16 rounded-xl bg-card animate-pulse" /><div className="h-64 rounded-xl bg-card animate-pulse" /></div>
  if (!data) return null
  const items: DayItemLite[] = (data.items ?? []) as any
  if (!data.connected && items.length === 0) return <ConnectPrompt />

  const today = dayStr(new Date())
  const { agenda, blocks, done } = groupDay(items as any[], day, today)
  const k = data.kpis ?? { dueToday: 0, overdue: 0, doneToday: 0, upcoming: 0 }
  const greet = user?.firstName || user?.username || ''
  const presentBlocks = BLOCK_SOURCES.filter((s) => (blocks[s]?.length ?? 0) > 0)
  const empty = agenda.length === 0 && presentBlocks.length === 0 && done.length === 0

  return (
    <div className="flex-1 min-w-0 h-full overflow-y-auto bg-background text-foreground">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b border-border/40 px-8 py-4">
        <h1 className="text-xl font-semibold">Good day{greet ? `, ${greet}` : ''}</h1>
        <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles size={14} className="text-primary" />
          {k.dueToday} due today, {k.overdue} overdue, {k.upcoming} upcoming{k.doneToday ? `, ${k.doneToday} done` : ''}.
        </p>
      </header>

      <main className="px-8 py-6 space-y-5">
        <div className="flex items-center gap-3">
          <button aria-label="Previous day" onClick={() => setDay(shiftDay(day, -1))} className="w-8 h-8 rounded-md bg-muted/50 hover:bg-accent flex items-center justify-center"><ChevronLeft size={16} /></button>
          <div className="text-sm font-semibold">{dayLabel(day, today)}</div>
          {day !== today && <button onClick={() => setDay(today)} className="text-[11px] text-primary hover:underline">jump to today</button>}
          <button aria-label="Next day" onClick={() => setDay(shiftDay(day, 1))} className="w-8 h-8 rounded-md bg-muted/50 hover:bg-accent flex items-center justify-center"><ChevronRight size={16} /></button>
          <span className="ml-auto text-[11px] text-muted-foreground/60">crossed-out = closed · open carries forward</span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Due today" value={k.dueToday} />
          <Kpi label="Overdue" value={k.overdue} tone="text-rose-400" />
          <Kpi label="Done today" value={k.doneToday} tone="text-emerald-400" />
          <Kpi label="Upcoming" value={k.upcoming} />
        </div>

        {efficiency && <EfficiencyChart snapshot={efficiency} />}

        {(data.commsTasks?.length ?? 0) > 0 && (
          <div className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border/40 text-xs font-semibold text-foreground">Created from your calls today</div>
            {data.commsTasks?.map((i: any) => (
              <a key={i.id} href={i.link} className="flex items-center gap-2 px-4 py-2 border-b border-border/40 last:border-0 hover:bg-accent/40 transition">
                <CheckSquare size={15} className="text-foreground/70 shrink-0" />
                <span className="flex-1 min-w-0 text-sm truncate">{i.title}</span>
              </a>
            ))}
          </div>
        )}

        {empty && <div className="rounded-xl bg-card ring-1 ring-border/40 px-4 py-10 text-center text-sm text-muted-foreground/70">Nothing on this day.</div>}

        {agenda.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-muted-foreground"><Timer size={14} /> Agenda · timed</div>
            <div className="rounded-xl bg-card ring-1 ring-border/40 p-2">
              {agenda.map((i, idx) => <AgendaRow key={i.id} item={i} last={idx === agenda.length - 1} />)}
            </div>
          </section>
        )}

        {presentBlocks.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-muted-foreground"><Inbox size={14} /> By source · no fixed time</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {presentBlocks.map((s) => <BlockCard key={s} source={s} items={blocks[s] as any[]} />)}
            </div>
          </section>
        )}

        {done.length > 0 && (
          <div className="rounded-xl bg-card ring-1 ring-border/40 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border/40 text-xs font-semibold text-muted-foreground">Done / earlier</div>
            {done.map((i: any) => (
              <a key={i.id} href={i.link} className="flex items-center gap-2 px-4 py-2 border-b border-border/40 last:border-0 hover:bg-accent/40 transition">
                <CheckSquare size={15} className="text-muted-foreground/60 shrink-0" />
                <span className="flex-1 min-w-0 text-sm truncate line-through text-muted-foreground">{i.title}</span>
                <span className="text-[11px] text-muted-foreground tabular-nums">{fmtTime(i.closedAt)}</span>
              </a>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}

/* ─────────────────── Legacy named exports (used by DashboardDrawer) ─────────────────── */

export function InteractionIcon({ type, i }: { type?: string; i?: Interaction }) {
  const t = type ?? i?.type
  const cls = 'shrink-0'
  switch (t) {
    case 'call':
      return <Phone size={12} className={`${cls} text-emerald-400`} />
    case 'meeting':
      return <Users size={12} className={`${cls} text-purple-400`} />
    case 'email_thread':
    case 'email_message':
      return <Mail size={12} className={`${cls} text-blue-400`} />
    case 'chat':
      return <MessageSquare size={12} className={`${cls} text-amber-400`} />
    case 'task':
      return <CheckCircle2 size={12} className={`${cls} text-foreground/70`} />
    case 'note':
      return <FileText size={12} className={`${cls} text-foreground/70`} />
    default:
      return <Sparkles size={12} className={`${cls} text-muted-foreground/80`} />
  }
}

export function dueColor(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (ms < 0) return 'bg-rose-500/20 text-rose-300'
  if (ms < 6 * 3600_000) return 'bg-amber-500/20 text-amber-300'
  if (ms < 24 * 3600_000) return 'bg-blue-500/20 text-blue-300'
  return 'bg-muted text-foreground/70'
}

export function ArtifactIcon({ kind }: { kind: 'recording' | 'transcript' | 'summary' | 'action_items' | 'doc' }) {
  switch (kind) {
    case 'recording': return <Mic size={12} />
    case 'transcript': return <FileText size={12} />
    case 'summary': return <Sparkles size={12} />
    case 'action_items': return <CheckCircle2 size={12} />
    default: return <Calendar size={12} />
  }
}

export function Avatar({ person, size = 'md' }: { person?: Person | { name: string; accent: string }; size?: 'xs' | 'sm' | 'md' }) {
  if (!person) return null
  const dim = size === 'xs' ? 'w-4 h-4 text-[8px]' : size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs'
  return (
    <span
      className={`${dim} rounded-full inline-flex items-center justify-center text-white font-bold ring-1 ring-border`}
      style={{ background: person.accent }}
    >
      {person.name.split(' ').map((s: string) => s[0]).slice(0, 2).join('')}
    </span>
  )
}

export function CompanyChip({ company }: { company: DashCompany }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full"
      style={{ background: `${company.accent}1f`, color: company.accent }}
    >
      <Briefcase size={10} /> {company.name}
    </span>
  )
}

export function ProjectChip({ project }: { project?: ProjectTrack }) {
  if (!project) return null
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-muted/50 text-foreground/70">
      {project.name}
    </span>
  )
}

export function PriorityDot({ priority }: { priority: Commitment['priority'] }) {
  const dot = priority === 'urgent' ? 'bg-rose-500' : priority === 'high' ? 'bg-amber-500' : priority === 'normal' ? 'bg-blue-500' : 'bg-muted-foreground/40'
  return <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
}

export function ProjectStatusPill({ status, health }: { status: ProjectTrack['status']; health: ProjectTrack['health'] }) {
  const tint =
    health === 'red'
      ? 'bg-rose-500/15 text-rose-400'
      : health === 'amber'
      ? 'bg-amber-500/15 text-amber-400'
      : 'bg-emerald-500/15 text-emerald-400'
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${tint} shrink-0`}>
      {status}
    </span>
  )
}

export function verbForType(t: Interaction['type']): string {
  switch (t) {
    case 'call': return 'ran a call:'
    case 'meeting': return 'met about'
    case 'email_thread': return 'replied on'
    case 'email_message': return 'replied on'
    case 'chat': return 'posted in'
    case 'task': return 'updated'
    case 'note': return 'added a note:'
    case 'document_review': return 'reviewed'
    case 'introduction': return 'made an intro:'
    default: return 'updated'
  }
}

// Re-export for the drawer.
export { Clock as ClockIcon }
